import { z } from "zod";

/** Suggestions return 0/1; fiche /entreprise usually uses booleans. */
const pappersFlag = z.preprocess((value) => {
  if (value === 0 || value === 1) return value === 1;
  return value;
}, z.boolean().nullable().optional());

const pappersText = z.preprocess(
  (value) =>
    Array.isArray(value) ? value.filter((part) => typeof part === "string").join(" ") : value,
  z.string().nullable().optional(),
);

const PappersPersonSchema = z.object({
  nom: z.string().nullable().optional(),
  prenom: pappersText,
  prenoms: pappersText,
  date_de_naissance: z.string().nullable().optional(),
  date_de_naissance_formate: z.string().nullable().optional(),
  ville_de_naissance: z.string().nullable().optional(),
  pays_de_naissance: z.string().nullable().optional(),
  code_pays_de_naissance: z.string().nullable().optional(),
  nationalite_iso: z.string().nullable().optional(),
  code_nationalite: z.string().nullable().optional(),
  qualite: z.string().nullable().optional(),
  adresse_ligne_1: z.string().nullable().optional(),
  adresse_ligne_2: z.string().nullable().optional(),
  adresse_ligne_3: z.string().nullable().optional(),
  code_postal: z.string().nullable().optional(),
  ville: z.string().nullable().optional(),
  code_pays: z.string().nullable().optional(),
  pourcentage_parts: z.number().nullable().optional(),
  pourcentage_votes: z.number().nullable().optional(),
  personne_morale: pappersFlag,
});

export const PappersSuggestionSchema = z.looseObject({
  siren: z.string(),
  nom_entreprise: z.string().nullable().optional(),
  denomination: z.string().nullable().optional(),
  code_naf: z.string().nullable().optional(),
  numero_tva_intracommunautaire: z.string().nullable().optional(),
  entreprise_cessee: pappersFlag,
  personne_morale: pappersFlag,
  siege: z
    .looseObject({
      siret: z.string().nullable().optional(),
      adresse_ligne_1: z.string().nullable().optional(),
      code_postal: z.string().nullable().optional(),
      ville: z.string().nullable().optional(),
      code_pays: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type PappersSuggestion = z.infer<typeof PappersSuggestionSchema>;

export const PappersSuggestionsResponseSchema = z.object({
  resultats_nom_entreprise: z.array(PappersSuggestionSchema).optional(),
  resultats_siren: z.array(PappersSuggestionSchema).optional(),
  resultats_siret: z.array(PappersSuggestionSchema).optional(),
});

const PappersFinanceSchema = z.object({
  chiffre_affaires: z.number().nullable().optional(),
  resultat: z.number().nullable().optional(),
});

const PappersCollectiveProcedurePublicationSchema = z.object({
  date: z.string().nullish(),
  date_jugement: z.string().nullish(),
  nature: z.string().nullish(),
  complement_jugement: z.string().nullish(),
});

const PappersCollectiveProcedureSchema = z.object({
  date_fin: z.string().nullish(),
  publications_bodacc: z.array(PappersCollectiveProcedurePublicationSchema).nullish(),
});

export const EntrepriseFicheSchema = z.object({
  siren: z.string(),
  procedure_collective_en_cours: z.boolean().nullable().optional(),
  procedures_collectives: z.array(PappersCollectiveProcedureSchema).nullish().catch(undefined),
  representants: z.array(PappersPersonSchema).nullable().optional(),
  dirigeants: z.array(PappersPersonSchema).nullable().optional(),
  beneficiaires_effectifs: z.array(PappersPersonSchema).nullable().optional(),
  nom_entreprise: z.string().nullable().optional(),
  denomination: z.string().nullable().optional(),
  categorie_juridique: z.string().nullable().optional(),
  date_creation: z.string().nullable().optional(),
  code_naf: z.string().nullable().optional(),
  capital: z.number().nullable().optional(),
  chiffre_affaires: z.number().nullable().optional(),
  resultat: z.number().nullable().optional(),
  tranche_effectif: z.string().nullable().optional(),
  effectif_max: z.number().nullable().optional(),
  numero_tva_intracommunautaire: z.string().nullable().optional(),
  telephone: z.string().nullable().optional(),
  siege: z
    .object({
      siret: z.string().nullable().optional(),
      adresse_ligne_1: z.string().nullable().optional(),
      adresse_ligne_2: z.string().nullable().optional(),
      adresse_ligne_3: z.string().nullable().optional(),
      code_postal: z.string().nullable().optional(),
      ville: z.string().nullable().optional(),
      code_pays: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  finances: z.array(PappersFinanceSchema).nullable().optional(),
  derniers_statuts: z
    .object({
      token: z.string().nullable().optional(),
      disponible: z.boolean().nullable().optional(),
    })
    .nullable()
    .optional(),
});

const PappersPersonLooseSchema = z
  .array(z.looseObject(PappersPersonSchema.shape))
  .nullable()
  .optional();

export const EntrepriseFicheLooseSchema = z.looseObject({
  ...EntrepriseFicheSchema.shape,
  representants: PappersPersonLooseSchema,
  dirigeants: PappersPersonLooseSchema,
  beneficiaires_effectifs: PappersPersonLooseSchema,
});

export type EntrepriseFiche = z.infer<typeof EntrepriseFicheSchema>;
